/**
 * Hook that wraps `useApexSnapshot` to surface the live Trident probe
 * reading relevant to the currently selected stock solution.
 *
 * Enhancement F: Apex Trident probe autofill.
 *
 * Returns `{ value: number | null; unit: string | null; stale: boolean }`.
 */
import { useMemo } from 'react';

import { probeKindForSolution, normaliseProbeValue } from '@reefapp/shared';
import type { ProbeKind, Solution } from '@reefapp/shared';

import { useApexSnapshot } from '../hooks/useApexSnapshot';

export interface LiveProbeInfo {
  value: number | null;
  unit: string | null;
  /** True when the snapshot hasn't updated in > 10 s (stale data). */
  stale: boolean;
}

const STALE_THRESHOLD_MS = 10_000;

export function useApexProbeForSolution(solution: Solution): LiveProbeInfo {
  const { snapshot } = useApexSnapshot();

  return useMemo(() => {
    const kind: ProbeKind | null = probeKindForSolution(solution);
    if (!kind || !snapshot?.apex?.probes) {
      return { value: null, unit: null, stale: false };
    }

    // Find the first Apex probe that matches the kind.
    // A Trident reports probes with name pattern like "Tank-pH", "Tank-Alk", "Tank-Ca", "Tank-Mg".
    const probe = snapshot.apex.probes.find((p) => {
      const nameUp = p.name.toUpperCase();
      if (kind === 'alk') return nameUp.includes('ALK') || nameUp.includes('KH');
      if (kind === 'ca') return nameUp.includes('CA') || nameUp.includes('CALCIUM');
      if (kind === 'mg') return nameUp.includes('MG') || nameUp.includes('MAGNESIUM');
      if (kind === 'ph') return nameUp.includes('PH');
      return false;
    });

    if (!probe) return { value: null, unit: null, stale: false };

    const fetchedAtMs = snapshot.apex.fetchedAtMs;
    const stale = Date.now() - fetchedAtMs > STALE_THRESHOLD_MS;

    return {
      value: normaliseProbeValue(probe.value),
      unit: probe.unit ?? solution.units ?? null,
      stale,
    };
  }, [solution, snapshot]);
}
